import React, { useState } from "react";
import client, { formatApiError } from "@/lib/api";
import {
  School, Mail, Phone, MapPin, Globe2, UserCircle2, Plus, Trash2,
  CheckCircle2, GraduationCap, Users, Send,
} from "lucide-react";

/**
 * The public "School Onboarding" link we send a school directly, so their own
 * staff fill in the principal, every teacher and every student themselves.
 *
 * No login — the person completing this at the school has no HikmahVerses
 * account yet, which is the whole point of sending a link. It posts to
 * POST /school-onboarding-submissions, which stores the roster in its own
 * collection; nothing goes live until an admin reviews it on the Admin CMS's
 * "School Onboarding" tab and imports it through the normal batch flow.
 */

const CLASS_OPTIONS = [
  "Grade 1", "Grade 2", "Grade 3", "Grade 4", "Grade 5",
  "Grade 6", "Grade 7", "Grade 8", "Grade 9", "Grade 10",
];
const SECTION_OPTIONS = ["A", "B", "C", "D", "E", "F"];
const MAX_TEACHERS = 30;
const MAX_STUDENTS = 100;

const EMPTY_TEACHER = { name: "", email: "", class_name: "", section: "" };
const EMPTY_STUDENT = { name: "", class_name: "", section: "", age: "", gender: "", parent_mobile: "" };

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const inputCls =
  "w-full px-4 py-3 min-h-[44px] rounded-full border-2 border-[#E5DFC8] bg-white text-hv-navy " +
  "focus:border-hv-teal focus:outline-none transition";
const selectCls = inputCls + " appearance-none";

function Field({ label, icon: Icon, children, required }) {
  return (
    <div>
      <label className="text-sm font-bold text-hv-navy inline-flex items-center gap-1.5">
        {Icon && <Icon size={14} className="text-hv-teal" strokeWidth={2.5} />}
        {label} {required && <span className="text-hv-coral">*</span>}
      </label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

export default function SchoolOnboardingForm() {
  const [school, setSchool] = useState({
    school_name: "", school_email: "", school_phone: "", address: "", city: "", country: "",
  });
  const [principal, setPrincipal] = useState({ principal_name: "", principal_email: "" });
  const [teachers, setTeachers] = useState([{ ...EMPTY_TEACHER }]);
  const [students, setStudents] = useState([{ ...EMPTY_STUDENT }]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [err, setErr] = useState("");

  const updateTeacher = (i, patch) =>
    setTeachers((rows) => rows.map((r, ri) => (ri === i ? { ...r, ...patch } : r)));
  const updateStudent = (i, patch) =>
    setStudents((rows) => rows.map((r, ri) => (ri === i ? { ...r, ...patch } : r)));

  // A new row inherits the last row's Class and Section — schools enter a whole
  // class back to back, and re-picking the same two dropdowns is exactly the
  // friction a link is meant to remove compared with a blank spreadsheet.
  const addTeacher = () => setTeachers((rows) => {
    if (rows.length >= MAX_TEACHERS) return rows;
    const last = rows[rows.length - 1];
    return [...rows, { ...EMPTY_TEACHER, class_name: last?.class_name || "", section: last?.section || "" }];
  });
  const addStudent = () => setStudents((rows) => {
    if (rows.length >= MAX_STUDENTS) return rows;
    const last = rows[rows.length - 1];
    return [...rows, { ...EMPTY_STUDENT, class_name: last?.class_name || "", section: last?.section || "" }];
  });
  const removeTeacher = (i) => setTeachers((rows) => (rows.length > 1 ? rows.filter((_, ri) => ri !== i) : rows));
  const removeStudent = (i) => setStudents((rows) => (rows.length > 1 ? rows.filter((_, ri) => ri !== i) : rows));

  const validTeachers = teachers.filter(
    (t) => t.name.trim() && EMAIL_RE.test(t.email.trim()) && t.class_name && t.section);
  const validStudents = students.filter(
    (s) => s.name.trim() && s.class_name && s.section && s.age && s.gender);

  const schoolValid = school.school_name.trim() && EMAIL_RE.test(school.school_email.trim())
    && school.school_phone.trim() && school.address.trim() && school.city.trim() && school.country.trim();
  const principalValid = principal.principal_name.trim() && EMAIL_RE.test(principal.principal_email.trim());
  const formValid = schoolValid && principalValid && validTeachers.length > 0 && validStudents.length > 0;

  const submit = async (e) => {
    e.preventDefault();
    setAttempted(true);
    setErr("");
    if (!formValid || submitting) {
      if (!formValid) setErr("A few things still need filling in — check the highlighted sections below.");
      return;
    }
    setSubmitting(true);
    try {
      await client.post("/school-onboarding-submissions", {
        ...school,
        school_email: school.school_email.trim().toLowerCase(),
        ...principal,
        principal_email: principal.principal_email.trim().toLowerCase(),
        teachers: validTeachers.map((t) => ({
          ...t, name: t.name.trim(), email: t.email.trim().toLowerCase(),
        })),
        students: validStudents.map((s) => ({
          ...s, name: s.name.trim(), age: Number(s.age),
          parent_mobile: s.parent_mobile.trim() || null,
        })),
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <main className="max-w-xl mx-auto px-5 sm:px-8 py-20" data-testid="onboarding-success">
        <div className="hv-card p-10 text-center">
          <div className="hv-icon-circle w-16 h-16 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle2 size={30} strokeWidth={2.5} />
          </div>
          <h1 className="hv-heading mt-6 text-3xl">
            <span className="text-hv-navy">Jazakum Allahu</span>{" "}
            <span className="text-hv-coral">khayran.</span>
          </h1>
          <p className="mt-4 text-hv-slate leading-relaxed">
            We have {school.school_name || "your school"}&apos;s details — {validTeachers.length} teacher
            {validTeachers.length === 1 ? "" : "s"} and {validStudents.length} student
            {validStudents.length === 1 ? "" : "s"}. Our team will review the roster and come back to you
            to set up classes and logins.
          </p>
          <p className="mt-4 text-hv-slate text-sm">
            Nothing is live yet — no accounts have been created from this form.
          </p>
        </div>
      </main>
    );
  }

  const invalid = (cond) => attempted && !cond;

  return (
    <main className="max-w-4xl mx-auto px-5 sm:px-8 py-10" data-testid="school-onboarding-form">
      <span className="hv-badge">
        <School size={13} className="text-hv-teal" strokeWidth={3} /> School Onboarding
      </span>
      <h1 className="hv-heading mt-5 text-3xl sm:text-4xl lg:text-5xl leading-[1.1]">
        <span className="text-hv-navy">Tell us about your</span>{" "}
        <span className="text-hv-teal">school</span>
        <span className="text-hv-navy">, and we&apos;ll do the</span>{" "}
        <span className="text-hv-coral">setting up.</span>
      </h1>
      <p className="mt-4 text-hv-slate leading-relaxed max-w-2xl">
        Fill this in once and we will create your classes and logins for you. You can add up to{" "}
        {MAX_TEACHERS} teachers and {MAX_STUDENTS} students. Nothing goes live until our team has
        reviewed it with you.
      </p>

      <form onSubmit={submit} className="mt-10 space-y-6">
        {/* SCHOOL */}
        <section className={`hv-card p-7 ${invalid(schoolValid) ? "ring-2 ring-hv-coral/50" : ""}`} data-testid="section-school">
          <span className="hv-badge">1 · Your school</span>
          <div className="mt-5 grid sm:grid-cols-2 gap-4">
            <Field label="School name" icon={School} required>
              <input value={school.school_name} data-testid="onb-school-name" className={inputCls}
                     onChange={(e) => setSchool((v) => ({ ...v, school_name: e.target.value }))} />
            </Field>
            <Field label="School email" icon={Mail} required>
              <input type="email" value={school.school_email} data-testid="onb-school-email" className={inputCls}
                     onChange={(e) => setSchool((v) => ({ ...v, school_email: e.target.value }))} />
            </Field>
            <Field label="School phone" icon={Phone} required>
              <input value={school.school_phone} data-testid="onb-school-phone" className={inputCls}
                     onChange={(e) => setSchool((v) => ({ ...v, school_phone: e.target.value }))} />
            </Field>
            <Field label="Address" icon={MapPin} required>
              <input value={school.address} data-testid="onb-address" className={inputCls}
                     onChange={(e) => setSchool((v) => ({ ...v, address: e.target.value }))} />
            </Field>
            <Field label="City" icon={MapPin} required>
              <input value={school.city} data-testid="onb-city" className={inputCls}
                     onChange={(e) => setSchool((v) => ({ ...v, city: e.target.value }))} />
            </Field>
            <Field label="Country" icon={Globe2} required>
              <input value={school.country} data-testid="onb-country" className={inputCls}
                     onChange={(e) => setSchool((v) => ({ ...v, country: e.target.value }))} />
            </Field>
          </div>
        </section>

        {/* PRINCIPAL */}
        <section className={`hv-card p-7 ${invalid(principalValid) ? "ring-2 ring-hv-coral/50" : ""}`} data-testid="section-principal">
          <span className="hv-badge">2 · Principal</span>
          <div className="mt-5 grid sm:grid-cols-2 gap-4">
            <Field label="Principal name" icon={UserCircle2} required>
              <input value={principal.principal_name} data-testid="onb-principal-name" className={inputCls}
                     onChange={(e) => setPrincipal((v) => ({ ...v, principal_name: e.target.value }))} />
            </Field>
            <Field label="Principal email" icon={Mail} required>
              <input type="email" value={principal.principal_email} data-testid="onb-principal-email" className={inputCls}
                     onChange={(e) => setPrincipal((v) => ({ ...v, principal_email: e.target.value }))} />
            </Field>
          </div>
        </section>

        {/* TEACHERS */}
        <section className={`hv-card p-7 ${invalid(validTeachers.length > 0) ? "ring-2 ring-hv-coral/50" : ""}`} data-testid="section-teachers">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="hv-badge">
              <GraduationCap size={13} className="text-hv-teal" strokeWidth={3} /> 3 · Teachers
            </span>
            <span className="text-hv-slate text-xs font-bold">{teachers.length} / {MAX_TEACHERS}</span>
          </div>
          <div className="mt-5 space-y-3">
            {teachers.map((t, i) => (
              <div key={i} className="grid sm:grid-cols-12 gap-2 items-center" data-testid={`onb-teacher-${i}`}>
                <input placeholder="Full name" value={t.name} data-testid={`onb-teacher-name-${i}`}
                       className={inputCls + " sm:col-span-3"}
                       onChange={(e) => updateTeacher(i, { name: e.target.value })} />
                <input placeholder="Email" type="email" value={t.email} data-testid={`onb-teacher-email-${i}`}
                       className={inputCls + " sm:col-span-4"}
                       onChange={(e) => updateTeacher(i, { email: e.target.value })} />
                <select value={t.class_name} data-testid={`onb-teacher-class-${i}`}
                        className={selectCls + " sm:col-span-3"}
                        onChange={(e) => updateTeacher(i, { class_name: e.target.value })}>
                  <option value="">Class…</option>
                  {CLASS_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={t.section} data-testid={`onb-teacher-section-${i}`}
                        className={selectCls + " sm:col-span-1"}
                        onChange={(e) => updateTeacher(i, { section: e.target.value })}>
                  <option value="">—</option>
                  {SECTION_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <button type="button" onClick={() => removeTeacher(i)} data-testid={`onb-teacher-remove-${i}`}
                        title="Remove this teacher"
                        className="sm:col-span-1 min-h-[44px] rounded-full text-hv-slate hover:text-hv-coral inline-flex items-center justify-center">
                  <Trash2 size={18} strokeWidth={2.5} />
                </button>
              </div>
            ))}
          </div>
          <button type="button" onClick={addTeacher} disabled={teachers.length >= MAX_TEACHERS}
                  data-testid="onb-add-teacher" className="hv-btn-secondary mt-4 inline-flex items-center gap-2 disabled:opacity-50">
            <Plus size={18} strokeWidth={2.5} /> Add another teacher
          </button>
        </section>

        {/* STUDENTS */}
        <section className={`hv-card p-7 ${invalid(validStudents.length > 0) ? "ring-2 ring-hv-coral/50" : ""}`} data-testid="section-students">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="hv-badge">
              <Users size={13} className="text-hv-teal" strokeWidth={3} /> 4 · Students
            </span>
            <span className="text-hv-slate text-xs font-bold">{students.length} / {MAX_STUDENTS}</span>
          </div>
          <div className="mt-5 space-y-3">
            {students.map((s, i) => (
              <div key={i} className="grid sm:grid-cols-12 gap-2 items-center" data-testid={`onb-student-${i}`}>
                <input placeholder="Full name" value={s.name} data-testid={`onb-student-name-${i}`}
                       className={inputCls + " sm:col-span-3"}
                       onChange={(e) => updateStudent(i, { name: e.target.value })} />
                <select value={s.class_name} data-testid={`onb-student-class-${i}`}
                        className={selectCls + " sm:col-span-2"}
                        onChange={(e) => updateStudent(i, { class_name: e.target.value })}>
                  <option value="">Class…</option>
                  {CLASS_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={s.section} data-testid={`onb-student-section-${i}`}
                        className={selectCls + " sm:col-span-1"}
                        onChange={(e) => updateStudent(i, { section: e.target.value })}>
                  <option value="">—</option>
                  {SECTION_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <input placeholder="Age" type="number" min="5" max="18" value={s.age} data-testid={`onb-student-age-${i}`}
                       className={inputCls + " sm:col-span-1"}
                       onChange={(e) => updateStudent(i, { age: e.target.value })} />
                <select value={s.gender} data-testid={`onb-student-gender-${i}`}
                        className={selectCls + " sm:col-span-2"}
                        onChange={(e) => updateStudent(i, { gender: e.target.value })}>
                  <option value="">Boy / Girl</option>
                  <option value="Boy">Boy</option>
                  <option value="Girl">Girl</option>
                </select>
                <input placeholder="Parent mobile" value={s.parent_mobile} data-testid={`onb-student-parent-${i}`}
                       className={inputCls + " sm:col-span-2"}
                       onChange={(e) => updateStudent(i, { parent_mobile: e.target.value })} />
                <button type="button" onClick={() => removeStudent(i)} data-testid={`onb-student-remove-${i}`}
                        title="Remove this student"
                        className="sm:col-span-1 min-h-[44px] rounded-full text-hv-slate hover:text-hv-coral inline-flex items-center justify-center">
                  <Trash2 size={18} strokeWidth={2.5} />
                </button>
              </div>
            ))}
          </div>
          <button type="button" onClick={addStudent} disabled={students.length >= MAX_STUDENTS}
                  data-testid="onb-add-student" className="hv-btn-secondary mt-4 inline-flex items-center gap-2 disabled:opacity-50">
            <Plus size={18} strokeWidth={2.5} /> Add another student
          </button>
        </section>

        {err && (
          <div className="hv-card p-5 text-hv-coral font-bold text-sm" data-testid="onb-error">{err}</div>
        )}

        <div className="flex flex-wrap items-center gap-4">
          <button type="submit" disabled={submitting} data-testid="onb-submit"
                  className="hv-btn-primary inline-flex items-center gap-2 disabled:opacity-60">
            <Send size={18} strokeWidth={2.5} /> {submitting ? "Sending…" : "Send our roster"}
          </button>
          <span className="text-hv-slate text-sm">
            {validTeachers.length} teacher{validTeachers.length === 1 ? "" : "s"} ·{" "}
            {validStudents.length} student{validStudents.length === 1 ? "" : "s"} ready to send
          </span>
        </div>
      </form>
    </main>
  );
}
